import React, { CSSProperties, useMemo, useRef } from "react";
import { Offset } from "../fe_types";
import {
  useBlobDragSelect,
  useOnWindowSizeChange,
  useSettings,
} from "../hooks";
import {
  PlatzBlobView,
  PlatzBlobViewTokensItem,
  SourceLocation,
} from "../api-generated";
import {
  copyToClipboard,
  getTokenListString,
  isInRangeOfArray,
  searchGoogle,
  searchGoogleTranslate,
} from "../utils";
import TextSelectionPopup from "./TextSelectionPopup";
import Token from "./Token";

interface BlobProps {
  blob: PlatzBlobView;
  location: SourceLocation;
  focusedTokenIndex?: number;
  playingTokenIndeces?: number[];
  tokensInMultiSelect?: number[];
  onTokenClick?: (token: PlatzBlobViewTokensItem, tokenIndex: number) => void;
  //TODO: is there a better/more idiomatic pattern than this overrideShowTranslations?
  overrideShowTranslations?: boolean;
  onTokenHover?: (tokenIndex: number) => void;
  onlyFocusOnHover?: boolean;
}

//ATOW: this component is concerned with:
//- calculating the position offsets of tokens, passing them to tokens
//- rendering the positioned container that is needed to render phrases
const Blob: React.FC<BlobProps> = ({
  blob,
  location,
  focusedTokenIndex,
  playingTokenIndeces,
  tokensInMultiSelect,
  onTokenClick,
  overrideShowTranslations,
  onTokenHover,
  onlyFocusOnHover = false,
}: BlobProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const tokensRef = useRef<HTMLElement[] | null>(null);
  const {
    highlightedTokenIndeces,
    clearSelectionClosePopup,
    isSelectionPopupOpen,
  } = useBlobDragSelect(containerRef, tokensRef);
  const { shouldShowLineTranslations: showTranslationsFromSettings } =
    useSettings();
  const shouldShowLineTranslations =
    overrideShowTranslations !== undefined
      ? overrideShowTranslations
      : showTranslationsFromSettings;

  const recalculateOffsets = (): null | Offset[] => {
    //if (!phraseAssociations || !tokensRef.current || !containerRef.current) {
    ////no need to do this calculation if there are no phrases in this blob
    //INTERESTING BUG: the above would mean that offsets would never be calculated
    //if there were no phrases initially - meaning highlightedTokensPositioning
    //wouldn't get set and the popup would never open when you were trying to
    //add the first phrase of the blob
    if (!tokensRef.current || !containerRef.current) {
      //no need to do this calculation if there are no phrases in this blob
      return null;
    }

    let newOffsets: Offset[] | null = null;
    const containerNode = containerRef.current;
    newOffsets = tokensRef.current.map((node) => {
      return {
        left: node.offsetLeft,
        right: containerNode.offsetWidth - (node.offsetLeft + node.offsetWidth),
        top: node.offsetTop,
      };
    });
    return newOffsets;
  };

  const calculatedOffsets = useMemo(recalculateOffsets, [
    tokensRef.current,
    containerRef.current,
  ]);

  //TODO: maybe there should be a debounce on recalculating on window
  //size change - it's a bit jarring
  useOnWindowSizeChange(recalculateOffsets);

  function getRefNodes() {
    if (!tokensRef.current) {
      tokensRef.current = [];
    }
    return tokensRef.current;
  }

  const onRefChange = (node: HTMLElement | null, index: number): void => {
    let nodes = getRefNodes();
    if (node) {
      nodes[index] = node;
    } else {
      const newArray = [...nodes];
      newArray.splice(index, 1);
      nodes = newArray;
    }
  };

  let highlightedTokensPositioning: CSSProperties | undefined = undefined;
  if (
    calculatedOffsets &&
    calculatedOffsets.length &&
    highlightedTokenIndeces.length
  ) {
    highlightedTokensPositioning = {
      top: calculatedOffsets[highlightedTokenIndeces[0]].top,
      left: calculatedOffsets[highlightedTokenIndeces[0]].left,
    };
  }

  const searchSelectionInGoogleTranslate = () => {
    clearSelectionClosePopup();
    if (highlightedTokenIndeces.length) {
      searchGoogleTranslate(
        blob.tokens.filter((_, i) => highlightedTokenIndeces.includes(i))
      );
    }
  };

  const searchSelectionInGoogle = () => {
    clearSelectionClosePopup();
    if (highlightedTokenIndeces.length) {
      searchGoogle(
        blob.tokens.filter((_, i) => highlightedTokenIndeces.includes(i))
      );
    }
  };

  const getHighlightedTokens = (): PlatzBlobViewTokensItem[] => {
    return blob.tokens.filter((_, i) => highlightedTokenIndeces.includes(i));
  };

  const getHighlightedTokensString = (): string | undefined => {
    const highlightedTokens = getHighlightedTokens();
    return highlightedTokens.length
      ? getTokenListString(highlightedTokens)
      : undefined;
  };

  const copySelectionToClipboard = () => {
    clearSelectionClosePopup();
    const highlightedTokensString = getHighlightedTokensString();
    if (!highlightedTokensString) {
      throw new Error("this shouldn't happen");
    }
    if (highlightedTokensString) {
      copyToClipboard(highlightedTokensString);
    }
  };

  //TODO: sometimes calculatedOffsets is null b/c either //tokensRef.current or containerRef.current is null,
  //it might only be happening in giant sources...?
  //if (phraseAssociations?.length) {
  //console.log("Blob got phrases", phraseAssociations);
  //console.log("calculatedOffsets", calculatedOffsets);
  //}

  //const shouldRenderPhrases = false;

  return (
    <div>
      <div
        className="blob relative select-none"
        style={{ lineHeight: 0.5 }}
        ref={containerRef}
      >
        {blob.tokens.map((t, tokenIndex) => (
          <Token
            key={`${location}-${tokenIndex}`}
            token={t}
            location={{ ...location, tokenIndex } as SourceLocation}
            isHighlightedForDragSelection={highlightedTokenIndeces.includes(
              tokenIndex
            )}
            isFocused={focusedTokenIndex === tokenIndex}
            onlyFocusOnHover={onlyFocusOnHover}
            onWordHover={() => {
              if (onTokenHover) {
                onTokenHover(tokenIndex);
              }
            }}
            isInMultiSelect={tokensInMultiSelect?.includes(tokenIndex)}
            refCallback={(node) => onRefChange(node, tokenIndex)}
            isCurrentlyPlaying={
              playingTokenIndeces
                ? isInRangeOfArray(tokenIndex, playingTokenIndeces)
                : false
            }
            onClick={() => {
              if (onTokenClick) {
                onTokenClick(t, tokenIndex);
              }
            }}
          />
        ))}
        <TextSelectionPopup
          isOpen={isSelectionPopupOpen}
          onClose={clearSelectionClosePopup}
          positionStyle={highlightedTokensPositioning}
          onTriggerSearchGoogle={searchSelectionInGoogle}
          onTriggerSearchGoogleTranslate={searchSelectionInGoogleTranslate}
          onTriggerCopyToClipboard={copySelectionToClipboard}
        />
      </div>
      {blob.englishSentenceTranslation && shouldShowLineTranslations && (
        <div className="text-xs text-gray-500 pb-1">
          {blob.englishSentenceTranslation}
        </div>
      )}
    </div>
  );
};

export default Blob;

interface NumberTuple {
  first: number;
  second: number;
}

interface ExplicitRange {
  min: number;
  max: number;
}

function isTuple(range: ExplicitRange | NumberTuple): range is NumberTuple {
  return (range as NumberTuple).first !== undefined;
}

export const isInRange = (
  target: number,
  range: ExplicitRange | NumberTuple
) => {
  let min, max;
  if (isTuple(range)) {
    min = range.first;
    max = range.second;
  } else {
    min = range.min;
    max = range.max;
  }
  return target >= min && target <= max;
};
